import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function OAuthContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">OAuth 认证</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 的 OAuth 令牌管理机制，包括令牌存储、Anthropic 订阅认证、OAuth 交换流程和多账户路由。
        </p>
      </div>

      <SectionTitle id="oauth-why">令牌汇聚点（为什么需要它）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        OpenClaw 使用统一的令牌汇聚点管理所有 OAuth 令牌，而不是让每个渠道独立管理认证。
        这样做的好处：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>统一的令牌刷新机制，避免令牌过期导致的中断</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>多账户支持，同一提供商可以配置多个账户</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>令牌安全存储在系统密钥链中，不暴露在配置文件里</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>支持故障转移，一个账户失效时自动切换到备用账户</span></li>
      </ul>

      <SectionTitle id="oauth-storage">存储（令牌存放位置）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OAuth 令牌存储在操作系统的安全密钥链中：
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">操作系统</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">存储位置</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['macOS', 'Keychain Access（钥匙串）'],
              ['Linux', 'libsecret / GNOME Keyring / KWallet'],
              ['Windows', 'Windows Credential Manager'],
            ].map(([os, location], i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3 text-slate-300 font-medium">{os}</td>
                <td className="py-2.5 px-3 text-slate-400">{location}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <InfoBox type="info">
        令牌不会存储在工作区文件中，因此不会被意外提交到 Git 仓库。
      </InfoBox>

      <SectionTitle id="oauth-anthropic">Anthropic setup-token（订阅认证）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Anthropic 支持通过 Claude.ai 订阅进行认证，无需单独申请 API 密钥：
      </p>
      <CodeBlock language="bash" code={`# 使用 Claude.ai 订阅认证
openclaw auth anthropic --method oauth

# 这会打开浏览器，引导你完成 OAuth 授权流程
# 授权完成后，令牌会自动保存到系统密钥链`} />
      <InfoBox type="tip">
        如果你已有 Claude.ai Pro 或 Team 订阅，可以使用 OAuth 方式认证，无需额外付费购买 API 额度。
      </InfoBox>

      <SectionTitle id="oauth-exchange">OAuth 交换（登录工作原理）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OAuth 认证流程：
      </p>
      <div className="space-y-3 my-4">
        {[
          '用户执行 openclaw auth 命令',
          'OpenClaw 生成 OAuth 授权 URL 并打开浏览器',
          '用户在浏览器中登录并授权',
          '提供商将授权码重定向到本地回调地址',
          'OpenClaw 使用授权码换取访问令牌和刷新令牌',
          '令牌加密存储到系统密钥链',
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span className="text-slate-300 text-sm">{step}</span>
          </div>
        ))}
      </div>

      <SectionTitle id="oauth-refresh">刷新 + 过期</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 自动管理令牌的刷新和过期：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-refresh-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>访问令牌过期前自动使用刷新令牌获取新的访问令牌</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-refresh-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>刷新令牌过期时，会提示用户重新授权</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-refresh-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>令牌刷新失败时，自动切换到备用账户（如果配置了故障转移）</span></li>
      </ul>
      <CodeBlock language="json5" code={`{
  auth: {
    // 令牌提前刷新时间（秒）
    // 在令牌过期前多少秒开始刷新
    refreshBeforeExpirySeconds: 300,
    // 刷新失败重试次数
    refreshRetries: 3,
    // 刷新失败重试间隔（秒）
    refreshRetryIntervalSeconds: 10,
  }
}`} />

      <SectionTitle id="oauth-multi">多账户（配置文件）+ 路由</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 支持为同一提供商配置多个账户，并通过配置文件（Profile）进行管理：
      </p>
      <CodeBlock language="json5" code={`{
  models: {
    anthropic: {
      // 默认账户
      default: {
        apiKey: "YOUR_ANTHROPIC_API_KEY_1",
      },
      // 备用账户
      backup: {
        apiKey: "YOUR_ANTHROPIC_API_KEY_2",
      },
      // 高级账户（用于特定模型）
      premium: {
        apiKey: "YOUR_ANTHROPIC_API_KEY_PREMIUM",
      }
    },
    
    // 路由规则：根据模型选择账户
    routing: [
      {
        // claude-3-opus 使用 premium 账户
        model: "claude-3-opus-*",
        profile: "premium",
      },
      {
        // 其他模型使用默认账户，失败时切换到 backup
        model: "*",
        profile: "default",
        fallback: "backup",
      }
    ]
  }
}`} />
      <InfoBox type="tip">
        多账户配置特别适合以下场景：不同模型使用不同账户、主账户达到速率限制时自动切换、按成本优化路由请求。
      </InfoBox>
    </div>
  );
}
